import React, {
    useEffect, useState, useCallback, useRef,
} from 'react';
import {
    Link, useLocation, useNavigate, useParams,
} from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import { authManager } from '../utils/auth';
import { isNonMmlAdmin } from '../utils/roles';
import { memberDisplayName } from '../Components/MemberSearchField';
import NotAvailable from '../Components/NotAvailable';
import {
    STATUS_OPTIONS, NO_ROWS, formatDollars, formatNumber, formatPct, commissaryLabel, stateLabel, csvCell,
    usePaged, TablePager, StatCard, GroupTable, TENDER_OPTIONS, ProductCodeCell, productScope,
} from './LoyaltyInsights';

const Base_Url = process.env.REACT_APP_API_BASE_URL;

// One member's receipts and every product they've bought
// (somo-circle1-api-admin#195, API: somo-circle1-api#788). CircleOne-only,
// like Receipt Insights: both API routes are behind requireNonMmlAdmin.

// Sent as `limit` to /members/:id/products; the API pages the list.
export const PRODUCTS_PAGE_SIZE = 20;
// The API maximum, for fetching every product for the CSV.
const CSV_PRODUCTS_PAGE_SIZE = 100;

const EMPTY_FILTERS = {
    from: '', to: '', status: 'accepted', tender: '',
};
const EMPTY_PRODUCT_QUERY = { search: '', sort: 'receipts', page: 1 };

const PRODUCT_SORTS = [
    { value: 'receipts', label: 'Most receipts' },
    { value: 'spend', label: 'Most spend' },
    { value: 'quantity', label: 'Most units' },
    { value: 'lastBought', label: 'Last bought' },
    { value: 'product', label: 'Name (A–Z)' },
];

const formatDate = (value) => (value ? new Date(value).toLocaleDateString(undefined, { timeZone: 'UTC' }) : '—');

// Date range and "Paid with" (somo-circle1-api#799): the scope the receipts
// queue drill-down carries too.
const dateParams = (filters) => {
    const params = {};
    if (filters.from) params.from = filters.from;
    if (filters.to) params.to = filters.to;
    if (filters.tender) params.tender = filters.tender;
    return params;
};

// The date/tender/status query params for a filter set.
export const filterParams = (filters) => ({ ...dateParams(filters), status: filters.status });

const productParams = (filters, query) => {
    const params = {
        ...filterParams(filters), sort: query.sort, page: query.page, limit: PRODUCTS_PAGE_SIZE,
    };
    if (query.search) params.search = query.search;
    return params;
};

export const buildMemberCsv = (summary, products) => {
    const { member, totals, frequency } = summary;
    const rows = [
        ['Member', memberDisplayName(member)],
        ['Email', member.email],
        ['Profile state', member.state],
        ['Receipts', totals.receiptCount],
        ['Total spend', totals.totalSpend],
        ['Avg receipt', totals.avgReceiptValue],
        ['Total SNAP', totals.totalSnap],
        ['Receipts paid with SNAP', totals.snapReceiptCount],
        ['Total WIC', totals.totalWic],
        ['Receipts paid with WIC', totals.wicReceiptCount],
        ['Total tax', totals.totalTax],
        ['Location mismatches', totals.locationMismatchCount],
        ['First receipt', frequency.firstReceiptAt],
        ['Last receipt', frequency.lastReceiptAt],
        ['Avg receipts per month', frequency.avgReceiptsPerMonth],
        ['Avg days between receipts', frequency.avgDaysBetweenReceipts],
        ['Products bought', summary.distinctProductCount],
        [],
        ['Product', 'SKU / UPC', 'Receipts', 'Units', 'Total spend', 'First bought', 'Last bought', 'Avg days between purchases'],
        ...products.map((p) => [
            p.product, p.productCode, p.receiptCount, p.totalQuantity, p.totalSpend, p.firstBoughtAt, p.lastBoughtAt,
            p.avgDaysBetweenPurchases,
        ]),
    ];
    return rows.map((row) => row.map(csvCell).join(',')).join('\n');
};

async function fetchData(path, params) {
    const res = await authManager.authenticatedFetch(`${Base_Url}/admin/loyalty/members/${path}?${new URLSearchParams(params)}`);
    // A gateway timeout's body is HTML or empty, not JSON.
    const body = await res.json().catch(() => null);
    if (!res.ok || body?.status !== 'success') {
        const error = new Error(body?.message || 'Failed to load this member\'s receipts.');
        error.status = res.status;
        throw error;
    }
    return body.data;
}

function LoyaltyMember() {
    const { id } = useParams();
    const location = useLocation();
    // The receipts queue's "Back to member" hands back the filters this page
    // had when the admin drilled down from it.
    const [initialFilters] = useState(() => ({ ...EMPTY_FILTERS, ...location.state?.restoreFilters }));
    const [allowed, setAllowed] = useState(null); // null = still checking
    const [filters, setFilters] = useState(initialFilters);
    const [applied, setApplied] = useState(initialFilters);
    const [summary, setSummary] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [loadError, setLoadError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [productQuery, setProductQuery] = useState(EMPTY_PRODUCT_QUERY);
    const [searchText, setSearchText] = useState('');
    const [products, setProducts] = useState(null);
    const [productsLoading, setProductsLoading] = useState(false);
    const [exporting, setExporting] = useState(false);

    // Only the latest request of each kind may update the page; leaving the
    // page abandons all of them, including an export part-way through.
    const latestSummaryRef = useRef(0);
    const latestProductsRef = useRef(0);
    const latestExportRef = useRef(0);
    useEffect(() => () => {
        latestSummaryRef.current += 1;
        latestProductsRef.current += 1;
        latestExportRef.current += 1;
    }, []);
    const navigate = useNavigate();
    const navigateRef = useRef(navigate);
    navigateRef.current = navigate;

    const loadProducts = useCallback(async (nextFilters, query) => {
        const requestId = ++latestProductsRef.current;
        setProductsLoading(true);
        try {
            const result = await fetchData(`${id}/products`, productParams(nextFilters, query));
            if (requestId !== latestProductsRef.current) return;
            setProductQuery(query);
            setProducts(result);
        } catch (err) {
            // A 404 is reported once, by the summary's "Member not found".
            if (requestId !== latestProductsRef.current || err.status === 404) return;
            console.error('Error fetching member products:', err);
            toast.error(err.message);
        } finally {
            if (requestId === latestProductsRef.current) setProductsLoading(false);
        }
    }, [id]);

    const load = useCallback(async (next) => {
        const requestId = ++latestSummaryRef.current;
        // Supersedes any product search/sort/page still in flight, since the
        // products committed below are for `next`.
        latestProductsRef.current += 1;
        setProductsLoading(false);
        setLoading(true);
        try {
            // Both or neither: a failed request leaves the totals and the
            // products as they were, so they never show different filters.
            const [result, productResult] = await Promise.all([
                fetchData(`${id}/receipt-summary`, filterParams(next)),
                fetchData(`${id}/products`, productParams(next, EMPTY_PRODUCT_QUERY)),
            ]);
            if (requestId !== latestSummaryRef.current) return;
            setApplied(next);
            setSummary(result);
            setProductQuery(EMPTY_PRODUCT_QUERY);
            setProducts(productResult);
            setSearchText('');
            setLoadError(null);
            // Keep this history entry on the filters now shown, so a refresh
            // or browser Back restores them.
            navigateRef.current('.', { replace: true, state: { restoreFilters: next } });
        } catch (err) {
            if (requestId !== latestSummaryRef.current) return;
            if (err.status === 404) {
                setNotFound(true);
            } else {
                console.error('Error fetching member receipts:', err);
                setLoadError(err.message);
            }
        } finally {
            if (requestId === latestSummaryRef.current) setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        (async () => {
            const currentUser = await authManager.validateUser().catch(() => null);
            if (!isNonMmlAdmin(currentUser)) {
                setAllowed(false);
                setLoading(false);
                return;
            }
            setAllowed(true);
            load(initialFilters);
        })();
    }, [load, initialFilters]);

    const setFilter = (field) => (e) => setFilters((prev) => ({ ...prev, [field]: e.target.value }));

    const handleApply = (e) => {
        e.preventDefault();
        if (filters.from && filters.to && filters.from > filters.to) {
            toast.error('The "from" date must be on or before the "to" date.');
            return;
        }
        load(filters);
    };

    const handleReset = () => {
        setFilters(EMPTY_FILTERS);
        load(EMPTY_FILTERS);
    };

    const handleProductSearch = (e) => {
        e.preventDefault();
        loadProducts(applied, { ...productQuery, search: searchText.trim(), page: 1 });
    };

    // Opens the receipts queue on this member's receipts (plus extraScope),
    // through its restoreFilters hand-off (LoyaltyReceipts.js).
    const receiptsLinkState = (extraScope = {}, label = '') => ({
        restoreFilters: {
            page: 1,
            status: applied.status,
            source: 'all',
            name: '',
            email: '',
            // The queue carries status separately.
            scope: { ...dateParams(applied), userId: id, ...extraScope },
            scopeLabel: label || `Member: ${memberDisplayName(summary.member)}`,
            // For the queue's "Back to member".
            memberReturn: { userId: id, name: memberDisplayName(summary.member), filters: applied },
        },
    });

    const handleExport = async () => {
        if (!summary) return;
        const exportId = ++latestExportRef.current;
        setExporting(true);
        try {
            const all = [];
            let page = 1;
            // Every product, not just the page on screen.
            for (;;) {
                // eslint-disable-next-line no-await-in-loop
                const result = await fetchData(`${id}/products`, {
                    ...filterParams(applied), sort: 'receipts', page, limit: CSV_PRODUCTS_PAGE_SIZE,
                });
                // Left the page: no more requests, and no download elsewhere.
                if (exportId !== latestExportRef.current) return;
                all.push(...result.products);
                if (all.length >= result.total || result.products.length === 0) break;
                page += 1;
            }
            const blob = new Blob([buildMemberCsv(summary, all)], { type: 'text/csv;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `member-receipts-${id}-${new Date().toISOString().slice(0, 10)}.csv`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            if (exportId !== latestExportRef.current) return;
            console.error('Error exporting member receipts:', err);
            toast.error(err.message);
        } finally {
            if (exportId === latestExportRef.current) setExporting(false);
        }
    };

    // Hooks, so above the early returns.
    const monthPages = usePaged(summary?.byMonth || NO_ROWS);

    if (allowed === false) return <NotAvailable />;

    if (notFound) {
        return (
            <div className="content pt-4">
                <div className="container-fluid">
                    <div className="card">
                        <div className="card-body text-center py-5">
                            <h5>Member not found.</h5>
                            <Link to="/loyaltyInsights">Back to Receipt Insights</Link>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const member = summary?.member;
    const totals = summary?.totals;
    const frequency = summary?.frequency;
    const maxMonthSpend = Math.max(0, ...(summary?.byMonth || []).map((m) => m.totalSpend || 0));
    const productTotalPages = products ? Math.max(1, Math.ceil(products.total / PRODUCTS_PAGE_SIZE)) : 1;

    return (
        <>
            <ToastContainer autoClose={3000} />
            <div className="content-header">
                <div className="container-fluid">
                    <div className="row mb-2 align-items-center">
                        <div className="col-sm-8">
                            <h1 className="m-0 txt_lft">
                                {member ? memberDisplayName(member) : 'Member receipts'}
                                {member?.deleted && <span className="badge badge-secondary ml-2 align-middle" style={{ fontSize: '0.9rem' }}>Deleted member</span>}
                            </h1>
                            {member && (
                                <div className="text-muted txt_lft" data-testid="member-header">
                                    {member.email}
                                    {' · '}
                                    <Link to={`/users/${id}`}>User profile</Link>
                                    {' · '}
                                    {`Profile state: ${member.state || '—'}`}
                                    {member.militaryBranchAffiliation && ` · ${member.militaryBranchAffiliation}`}
                                    {member.militaryStatus && ` · ${member.militaryStatus}`}
                                    {member.nearestBase && ` · Nearest base: ${member.nearestBase.name}`}
                                </div>
                            )}
                        </div>
                        <div className="col-sm-4 text-sm-right">
                            <Link to="/loyaltyInsights" className="btn btn-outline-secondary mr-2">
                                <i className="fa-solid fa-chart-column" /> Receipt Insights
                            </Link>
                            <button
                                type="button"
                                className="btn btn-outline-secondary"
                                onClick={handleExport}
                                disabled={!summary || loading || exporting}
                            >
                                <i className="fa-solid fa-download" /> {exporting ? 'Exporting…' : 'Export CSV'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="content">
                <div className="container-fluid">
                    <div className="card mb-3">
                        <form className="card-body" onSubmit={handleApply}>
                            <div className="row align-items-end">
                                <div className="col-md-2">
                                    <label htmlFor="member-from">From</label>
                                    <input id="member-from" type="date" className="form-control" value={filters.from} onChange={setFilter('from')} />
                                </div>
                                <div className="col-md-2">
                                    <label htmlFor="member-to">To</label>
                                    <input id="member-to" type="date" className="form-control" value={filters.to} onChange={setFilter('to')} />
                                </div>
                                <div className="col-md-2">
                                    <label htmlFor="member-tender">Paid with</label>
                                    <select id="member-tender" className="form-control" value={filters.tender} onChange={setFilter('tender')}>
                                        {TENDER_OPTIONS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                                    </select>
                                </div>
                                <div className="col-md-3">
                                    <label htmlFor="member-status">Status</label>
                                    <select id="member-status" className="form-control" value={filters.status} onChange={setFilter('status')}>
                                        {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                                    </select>
                                </div>
                                <div className="col-md-3 d-flex" style={{ gap: '8px' }}>
                                    <button
                                        type="submit"
                                        className="btn btn-primary flex-fill"
                                        style={{ backgroundColor: 'var(--primary)', borderColor: 'var(--primary)' }}
                                        disabled={loading}
                                    >
                                        Apply
                                    </button>
                                    <button type="button" className="btn btn-outline-secondary flex-fill" onClick={handleReset} disabled={loading}>
                                        Reset
                                    </button>
                                </div>
                            </div>
                            <small className="text-muted d-block mt-2">
                                No dates means the member&apos;s whole history. Dates are the purchase date printed on the receipt,
                                or the submission date when it couldn&apos;t be read.
                            </small>
                        </form>
                    </div>

                    {loading && (
                        <div className="text-center p-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="sr-only">Loading...</span>
                            </div>
                        </div>
                    )}

                    {!loading && loadError && (
                        <div className="alert alert-danger d-flex flex-wrap align-items-center justify-content-between" role="alert" style={{ gap: '8px' }}>
                            <span>
                                {summary
                                    ? `Couldn't apply these filters: ${loadError} Showing the previous results.`
                                    : `Couldn't load this member's receipts: ${loadError}`}
                            </span>
                            <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => load(filters)}>
                                Retry
                            </button>
                        </div>
                    )}

                    {!loading && summary && totals.receiptCount === 0 && (
                        <div className="card mb-3">
                            <div className="card-body text-center py-5">
                                <h5>This member has no receipts matching these filters.</h5>
                            </div>
                        </div>
                    )}

                    {!loading && summary && totals.receiptCount > 0 && (
                        <>
                            <div className="row" data-testid="member-totals">
                                <StatCard
                                    label="Receipts"
                                    value={formatNumber(totals.receiptCount)}
                                    linkState={receiptsLinkState()}
                                    title="View these receipts"
                                />
                                <StatCard label="Total spend" value={formatDollars(totals.totalSpend)} />
                                <StatCard label="Avg receipt" value={formatDollars(totals.avgReceiptValue)} />
                                <StatCard
                                    label="Total SNAP"
                                    value={formatDollars(totals.totalSnap)}
                                    hint={totals.snapReceiptCount != null ? `on ${formatNumber(totals.snapReceiptCount)} receipts` : null}
                                />
                                {totals.wicReceiptCount != null && (
                                    <StatCard
                                        label="Paid with WIC"
                                        value={`${formatNumber(totals.wicReceiptCount)} receipts`}
                                        hint={totals.totalWic ? `${formatDollars(totals.totalWic)} where the amount was read` : null}
                                    />
                                )}
                                <StatCard label="First receipt" value={formatDate(frequency.firstReceiptAt)} />
                                <StatCard
                                    label="Last receipt"
                                    value={formatDate(frequency.lastReceiptAt)}
                                    hint={frequency.daysSinceLastReceipt != null ? `${formatNumber(frequency.daysSinceLastReceipt)} days ago` : null}
                                />
                                <StatCard
                                    label="Avg receipts per month"
                                    value={formatNumber(frequency.avgReceiptsPerMonth)}
                                    hint={`active in ${formatNumber(frequency.activeMonthCount)} month${frequency.activeMonthCount === 1 ? '' : 's'}`}
                                />
                                <StatCard label="Avg days between receipts" value={formatNumber(frequency.avgDaysBetweenReceipts)} />
                                <StatCard label="Products bought" value={formatNumber(summary.distinctProductCount)} hint="different products" />
                                <StatCard
                                    label="Out-of-state shops"
                                    value={formatNumber(totals.locationMismatchCount)}
                                    hint={`${formatPct(totals.locationMismatchPct)} of receipts with both states known`}
                                />
                            </div>

                            <div className="card mb-3">
                                <div className="card-header"><h3 className="card-title">By month</h3></div>
                                <div className="card-body table-responsive p-0">
                                    <table className="table table-striped mb-0 txt_lft">
                                        <thead>
                                            <tr>
                                                <th>Month</th>
                                                <th>Receipts</th>
                                                <th>Avg receipt</th>
                                                <th style={{ width: '50%' }}>Total spend</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {monthPages.pageRows.map((m) => (
                                                <tr key={m.month}>
                                                    <td>{m.month}</td>
                                                    <td>{formatNumber(m.receiptCount)}</td>
                                                    <td>{formatDollars(m.avgReceiptValue)}</td>
                                                    <td>
                                                        <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                                                            <div
                                                                aria-hidden="true"
                                                                style={{
                                                                    height: '10px',
                                                                    borderRadius: '2px',
                                                                    background: 'var(--primary)',
                                                                    width: `${maxMonthSpend ? Math.round(((m.totalSpend || 0) / maxMonthSpend) * 70) : 0}%`,
                                                                }}
                                                            />
                                                            <span>{formatDollars(m.totalSpend)}</span>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <TablePager {...monthPages} label="By month" />
                            </div>

                            <div className="row">
                                <div className="col-xl-7">
                                    <GroupTable
                                        title="By commissary"
                                        groupHeader="Commissary"
                                        rows={summary.byCommissary}
                                        rowKey={(row) => row.commissaryBaseId || 'unknown'}
                                        rowLabel={commissaryLabel}
                                        showMembers={false}
                                        linkState={(row) => receiptsLinkState(
                                            { commissaryBaseId: row.commissaryBaseId || 'unknown' },
                                            `${memberDisplayName(member)} at ${commissaryLabel(row)}`,
                                        )}
                                    />
                                </div>
                                <div className="col-xl-5">
                                    {/* Next to the profile state, so a likely move stands
                                        out. A flag only (somo-circle1-api#777). */}
                                    <GroupTable
                                        title="By state"
                                        subtitle={`profile state: ${member.state || 'not set'}`}
                                        groupHeader="State"
                                        rows={summary.byState}
                                        rowKey={(row) => row.state || 'unknown'}
                                        rowLabel={(row) => stateLabel(row.state)}
                                        showMembers={false}
                                        linkState={(row) => receiptsLinkState(
                                            { state: row.state || 'unknown' },
                                            `${memberDisplayName(member)} in ${stateLabel(row.state)}`,
                                        )}
                                    />
                                </div>
                            </div>

                            <div className="card mb-3" data-testid="member-products">
                                <div className="card-header">
                                    <h3 className="card-title">Products bought</h3>
                                    <small className="text-muted ml-2">grouped by SKU/UPC where one is printed</small>
                                </div>
                                <div className="card-body pb-0">
                                    <form className="row align-items-end" onSubmit={handleProductSearch}>
                                        <div className="col-md-6 mb-3">
                                            <label htmlFor="member-product-search">Search products</label>
                                            <input
                                                id="member-product-search"
                                                type="search"
                                                className="form-control"
                                                value={searchText}
                                                onChange={(e) => setSearchText(e.target.value)}
                                                placeholder="e.g. MILK or a UPC"
                                            />
                                        </div>
                                        <div className="col-md-2 mb-3">
                                            <button type="submit" className="btn btn-outline-secondary btn-block">Search</button>
                                        </div>
                                        <div className="col-md-4 mb-3">
                                            <label htmlFor="member-product-sort">Sort by</label>
                                            <select
                                                id="member-product-sort"
                                                className="form-control"
                                                value={productQuery.sort}
                                                onChange={(e) => loadProducts(applied, { ...productQuery, sort: e.target.value, page: 1 })}
                                            >
                                                {PRODUCT_SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                                            </select>
                                        </div>
                                    </form>
                                </div>
                                <div className="card-body table-responsive p-0" style={productsLoading ? { opacity: 0.6 } : undefined}>
                                    {products && products.products.length === 0 && (
                                        <p className="text-muted p-3 mb-0">
                                            {productQuery.search ? 'No products match this search.' : 'No line items were read from these receipts.'}
                                        </p>
                                    )}
                                    {products && products.products.length > 0 && (
                                        <table className="table table-striped mb-0 txt_lft">
                                            <thead>
                                                <tr>
                                                    <th>Product</th>
                                                    <th>SKU / UPC</th>
                                                    <th>Receipts</th>
                                                    <th>Units</th>
                                                    <th>Total spend</th>
                                                    <th>First bought</th>
                                                    <th>Last bought</th>
                                                    <th>Avg days between</th>
                                                    <th aria-label="Actions" />
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {products.products.map((p) => (
                                                    <tr key={p.productCode || `name:${p.product}`}>
                                                        <td className="fwt_600">{p.product}</td>
                                                        <td><ProductCodeCell row={p} /></td>
                                                        <td>{formatNumber(p.receiptCount)}</td>
                                                        <td>{formatNumber(p.totalQuantity)}</td>
                                                        <td>{formatDollars(p.totalSpend)}</td>
                                                        <td>{formatDate(p.firstBoughtAt)}</td>
                                                        <td>{formatDate(p.lastBoughtAt)}</td>
                                                        <td>{formatNumber(p.avgDaysBetweenPurchases)}</td>
                                                        <td className="text-right">
                                                            <Link
                                                                to="/loyaltyReceipts"
                                                                state={receiptsLinkState(productScope(p), `${memberDisplayName(member)}: ${p.product}`)}
                                                            >
                                                                Receipts
                                                            </Link>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                                {products && (
                                    <TablePager
                                        page={productQuery.page}
                                        totalPages={productTotalPages}
                                        total={products.total}
                                        pageSize={PRODUCTS_PAGE_SIZE}
                                        setPage={(page) => loadProducts(applied, { ...productQuery, page })}
                                        label="Products bought"
                                    />
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </>
    );
}

export default LoyaltyMember;
